import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import i18n from '../../../i18n';
import { Trans } from 'react-i18next';
import { AppLayout, PageBackLink } from '../../../components/layout';
import '../inventory.css';
import {
  fetchProductById,
  createProduct,
  updateProduct,
  fetchAddProductFormLookups,
  searchPartNumberSuggestions,
} from '../../../services/productCatalogService';
import MetricTreeSelect from '../components/MetricTreeSelect';

const TOAST_KEY = 'product_catalog_toast';

function translateProductStatusLabel(value, fallbackLabel) {
  if (value === 'Active') return i18n.t('status.active');
  if (value === 'Inactive') return i18n.t('status.inactive');
  if (value === 'End of Production') return i18n.t('inventory.endOfProduction');
  return fallbackLabel || value;
}

/** Digits + optional single decimal (prices). */
function sanitizeDecimalInput(raw) {
  const cleaned = String(raw || '').replace(/[^\d.]/g, '');
  const parts = cleaned.split('.');
  if (parts.length <= 1) return cleaned;
  return `${parts[0]}.${parts.slice(1).join('')}`;
}

/** Digits only (qty fields). */
function sanitizeIntegerInput(raw) {
  return String(raw || '').replace(/\D/g, '');
}

/** HSN: digits only, max 8. */
function sanitizeHsnInput(raw) {
  return String(raw || '').replace(/\D/g, '').slice(0, 8);
}

export default function AddEditProduct() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const isEditRoute = Boolean(productId);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [lookupsError, setLookupsError] = useState(null);
  const [lookupsLoading, setLookupsLoading] = useState(true);

  const [categories, setCategories] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [primaryVendors, setPrimaryVendors] = useState([]);
  const [secondaryVendors, setSecondaryVendors] = useState([]);
  const [abcOptions, setAbcOptions] = useState([]);
  const [metricCategories, setMetricCategories] = useState([]);

  const [name, setName] = useState('');
  const [partNumber, setPartNumber] = useState('');
  const [partSuggestions, setPartSuggestions] = useState([]);
  const [partSuggestOpen, setPartSuggestOpen] = useState(false);
  const [partSuggestLoading, setPartSuggestLoading] = useState(false);
  const partSuggestRef = useRef(null);

  const [category, setCategory] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [status, setStatus] = useState('');
  const [metricId, setMetricId] = useState('');
  const [metricCategory, setMetricCategory] = useState('');
  const [metricLabel, setMetricLabel] = useState('');
  const [listPrice, setListPrice] = useState('');
  const [unitRate, setUnitRate] = useState('');
  const [moq, setMoq] = useState('');
  const [reorderQuantity, setReorderQuantity] = useState('');
  const [maintenanceStockQuantity, setMaintenanceStockQuantity] = useState('');
  const [hsnCode, setHsnCode] = useState('');
  const [preferredVendor1Id, setPreferredVendor1Id] = useState('');
  const [preferredVendor2Id, setPreferredVendor2Id] = useState('');
  const [abcCategory, setAbcCategory] = useState('');
  const [description, setDescription] = useState('');
  const [productServiceId, setProductServiceId] = useState('');

  const [errors, setErrors] = useState({});
  const [validationAlert, setValidationAlert] = useState(null);

  const statusSelectOptions = useMemo(() => {
    const list = [...(statusOptions || [])];
    if (status && !list.some((o) => o.value === status)) {
      list.unshift({ value: status, label: status });
    }
    return list;
  }, [statusOptions, status]);

  // Category (search/service), Status (search/product-status), and other form lookups.
  useEffect(() => {
    let cancelled = false;

    async function loadLookups() {
      setLookupsLoading(true);
      setLookupsError(null);
      try {
        const lookups = await fetchAddProductFormLookups({ size: 100 });
        if (cancelled) return;

        setCategories(lookups.categories || []);
        setStatusOptions(lookups.statusOptions || []);
        setAbcOptions(lookups.abcOptions || []);
        setMetricCategories(lookups.metricCategories || []);
        setPrimaryVendors(lookups.primaryVendors || []);
        setSecondaryVendors(lookups.secondaryVendors || []);

        if (lookups.errors?.length) {
          setLookupsError(lookups.errors.join(' · '));
        }

        // Default status on Add when API returned options.
        if (!isEditRoute) {
          setStatus((prev) => {
            if (prev) return prev;
            const active = lookups.statusOptions?.find((o) => o.value === 'Active');
            return active?.value || lookups.statusOptions?.[0]?.value || '';
          });
        }
      } catch (err) {
        if (!cancelled) {
          setLookupsError(err?.message || i18n.t('inventory.failedToLoadDropdownOptions'));
          setCategories([]);
          setStatusOptions([]);
          setAbcOptions([]);
          setMetricCategories([]);
          setPrimaryVendors([]);
          setSecondaryVendors([]);
        }
      } finally {
        if (!cancelled) setLookupsLoading(false);
      }
    }

    loadLookups();
    return () => {
      cancelled = true;
    };
  }, [isEditRoute]);

  // Part Number → GET /product-catalog/search/part-number?searchTerm=…
  useEffect(() => {
    const term = partNumber.trim();
    if (term.length < 1) {
      setPartSuggestions([]);
      setPartSuggestOpen(false);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(() => {
      setPartSuggestLoading(true);
      searchPartNumberSuggestions(term, { size: 10 })
        .then((list) => {
          if (cancelled) return;
          setPartSuggestions(list || []);
          setPartSuggestOpen((list || []).length > 0);
        })
        .catch(() => {
          if (!cancelled) {
            setPartSuggestions([]);
            setPartSuggestOpen(false);
          }
        })
        .finally(() => {
          if (!cancelled) setPartSuggestLoading(false);
        });
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [partNumber]);

  useEffect(() => {
    function onDocClick(e) {
      if (!partSuggestRef.current?.contains(e.target)) setPartSuggestOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Edit product load
  useEffect(() => {
    if (!isEditRoute) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;

    async function load() {
      setLoading(true);
      setLoadError(null);
      try {
        const product = await fetchProductById(productId);
        if (cancelled) return;
        if (!product) {
          setLoadError(i18n.t('validation.productNotFound'));
          return;
        }
        setName(product.name || '');
        setPartNumber(product.partNumber || '');
        setCategory(product.category || '');
        setManufacturer(product.manufacturer || '');
        setStatus(product.status === 'Inactive' ? 'Active' : product.status || '');
        setMetricId(product.metricId || '');
        setMetricCategory(product.metricCategory || '');
        setMetricLabel(product.unitOfMeasure || '');
        setListPrice(
          product.listPrice !== undefined && product.listPrice !== null ? product.listPrice : ''
        );
        setUnitRate(
          product.unitRate !== undefined && product.unitRate !== null ? product.unitRate : ''
        );
        setMoq(product.moq !== undefined && product.moq !== null ? product.moq : '');
        setReorderQuantity(
          product.reorderQuantity !== undefined && product.reorderQuantity !== null
            ? product.reorderQuantity
            : ''
        );
        setMaintenanceStockQuantity(
          product.maintenanceStockQuantity !== undefined && product.maintenanceStockQuantity !== null
            ? product.maintenanceStockQuantity
            : ''
        );
        setHsnCode(product.hsnCode || '');
        setPreferredVendor1Id(product.preferredVendor1Id || '');
        setPreferredVendor2Id(product.preferredVendor2Id || '');
        setAbcCategory(product.abcCategory || '');
        setDescription(product.description || '');
        if (product.productServiceId != null) {
          setProductServiceId(String(product.productServiceId));
        }
        if (product.productServiceId != null || product.category) {
          setCategories((prev) => {
            const id = product.productServiceId != null ? String(product.productServiceId) : '';
            const name = product.category || i18n.t('inventory.serviceNumber', { id });
            if (!id) {
              // Legacy: only category name — keep as synthetic option so select can show it
              if (prev.some((c) => c.name === product.category)) return prev;
              return [
                ...prev,
                { id: product.category, productServiceId: null, name: product.category, label: product.category },
              ];
            }
            if (prev.some((c) => String(c.id) === id)) return prev;
            return [...prev, { id, productServiceId: Number(id), name, label: name }];
          });
        }
        if (product.preferredVendor1Id) {
          setPrimaryVendors((prev) => {
            if (prev.some((v) => String(v.id) === String(product.preferredVendor1Id))) return prev;
            const name = product.preferredVendor1 || i18n.t('inventory.vendorNumber', { id: product.preferredVendor1Id });
            return [...prev, { id: String(product.preferredVendor1Id), name, label: name }];
          });
        }
        if (product.preferredVendor2Id) {
          setSecondaryVendors((prev) => {
            if (prev.some((v) => String(v.id) === String(product.preferredVendor2Id))) return prev;
            const name = product.preferredVendor2 || i18n.t('inventory.vendorNumber', { id: product.preferredVendor2Id });
            return [...prev, { id: String(product.preferredVendor2Id), name, label: name }];
          });
        }
        if (product.abcCategory) {
          setAbcOptions((prev) => {
            if (prev.some((o) => o.value === product.abcCategory)) return prev;
            return [
              ...prev,
              { value: product.abcCategory, label: i18n.t('inventory.gradeLabel', { abcCategory: product.abcCategory }) },
            ];
          });
        }
      } catch (err) {
        if (!cancelled) setLoadError(err?.message || i18n.t('inventory.failedToLoadProduct'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [isEditRoute, productId]);

  const onCancel = () => navigate('/IMS/inventory/products');

  const handleCategoryChange = (serviceId) => {
    setProductServiceId(serviceId);
    const selected = categories.find((c) => String(c.id) === String(serviceId));
    setCategory(selected?.name || selected?.label || '');
    if (errors.category) setErrors({ ...errors, category: null });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!name.trim()) newErrors.name = i18n.t('inventory.productNameMandatory');
    if (!partNumber.trim()) newErrors.partNumber = i18n.t('inventory.partNumberMandatory');
    if (!productServiceId && !category) newErrors.category = i18n.t('inventory.categoryMandatory');
    if (!manufacturer.trim()) newErrors.manufacturer = i18n.t('inventory.manufacturerMandatory');
    if (!status) newErrors.status = i18n.t('inventory.statusMandatory');
    if (!metricId) newErrors.unitOfMeasure = i18n.t('inventory.unitOfMeasureMandatory');

    if (listPrice === '' || listPrice === undefined || isNaN(Number(listPrice)) || Number(listPrice) <= 0) {
      newErrors.listPrice = i18n.t('inventory.listPriceMandatoryHint');
    }
    if (unitRate === '' || unitRate === undefined || isNaN(Number(unitRate)) || Number(unitRate) < 0) {
      newErrors.unitRate = i18n.t('inventory.unitRateMandatory');
    }
    if (hsnCode && !/^\d{1,8}$/.test(hsnCode)) {
      newErrors.hsnCode = i18n.t('inventory.hsnCodeDigitsOnly');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setValidationAlert(i18n.t('inventory.fillAllMandatoryFields'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const selectedCategory = categories.find((c) => String(c.id) === String(productServiceId));
    const formPayload = {
      name: name.trim(),
      partNumber: partNumber.trim(),
      category: selectedCategory?.name || category,
      manufacturer: manufacturer.trim(),
      status,
      metricId,
      metricCategory: metricCategory || undefined,
      listPrice: Number(listPrice),
      unitRate: Number(unitRate),
      moq: moq !== '' ? Number(moq) : undefined,
      reorderQuantity: reorderQuantity !== '' ? Number(reorderQuantity) : undefined,
      maintenanceStockQuantity:
        maintenanceStockQuantity !== '' ? Number(maintenanceStockQuantity) : undefined,
      hsnCode: hsnCode || undefined,
      preferredVendor1Id: preferredVendor1Id || undefined,
      preferredVendor2Id: preferredVendor2Id || undefined,
      abcCategory: abcCategory || undefined,
      description: description.trim() || undefined,
      productServiceId: productServiceId || undefined,
    };

    setSaving(true);
    setValidationAlert(null);
    try {
      const saved = isEditRoute
        ? await updateProduct(productId, formPayload)
        : await createProduct(formPayload);

      const savedId = saved?.id || saved?.productId || productId;
      sessionStorage.setItem(
        TOAST_KEY,
        JSON.stringify({
          message: isEditRoute
            ? i18n.t('inventory.productUpdatedSuccess')
            : i18n.t('inventory.productCreatedSuccess'),
          type: 'success',
        })
      );
      navigate(`/IMS/inventory/products/${savedId}`);
    } catch (err) {
      setValidationAlert(err?.message || i18n.t('inventory.failedToSaveProduct'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  if (loading || lookupsLoading) {
    return (
      <AppLayout>
        <div className="inventory-module">
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <p style={{ color: 'var(--text-grey)', fontSize: '14px' }}>
              {loading ? i18n.t('inventory.loadingProduct') : i18n.t('inventory.loadingFormOptions')}
            </p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (loadError) {
    return (
      <AppLayout>
        <div className="inventory-module">
          <div style={{ padding: '40px', textAlign: 'center', backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid var(--border-color)' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--primary-navy)' }}>{loadError}</h3>
            <button type="button" className="btn-secondary" style={{ marginTop: '16px' }} onClick={() => navigate('/IMS/inventory/products')}>
              <Trans i18nKey="inventory.backToCatalog" />
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="inventory-module">
    <div style={{ padding: '0 8px' }}>
      <div style={{ marginBottom: '20px' }}>
        <PageBackLink to="/IMS/inventory/products" />
        <div>
          <h1 className="page-title">
            {isEditRoute ? <Trans i18nKey="inventory.editProduct" /> : <Trans i18nKey="formActions.addProduct" />}
          </h1>
        </div>
      </div>

      {lookupsError && (
        <div className="form-warning-alert" style={{ marginBottom: '16px', display: 'flex', gap: '12px', padding: '14px 16px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', color: '#92400e', alignItems: 'center' }}>
          <AlertTriangle size={20} style={{ flexShrink: 0 }} />
          <span style={{ fontSize: '14px', fontWeight: '600' }}>
            {i18n.t('inventory.someDropdownOptionsFailed', { lookupsError })}
          </span>
        </div>
      )}

      {validationAlert && (
        <div className="form-warning-alert" style={{ marginBottom: '24px', display: 'flex', gap: '12px', padding: '14px 16px', backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', color: '#991b1b', alignItems: 'center' }}>
          <AlertTriangle size={20} style={{ flexShrink: 0 }} />
          <span style={{ fontSize: '14px', fontWeight: '600' }}>{validationAlert}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="form-container-constrained" style={{ backgroundColor: '#ffffff', padding: '32px', borderRadius: '16px', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-sm)' }}>

        <div className="inv-responsive-grid">
          <div className="form-group">
            <label className="form-label" style={{ fontWeight: '600', color: 'var(--text-dark)' }}><Trans i18nKey="inventory.productName" /> *</label>
            <input
              type="text"
              className={`form-input ${errors.name ? 'form-input-error' : ''}`}
              placeholder={i18n.t('inventory.productNamePlaceholder')}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) setErrors({ ...errors, name: null });
              }}
              style={{ borderColor: errors.name ? 'var(--accent-red)' : '' }}
            />
            {errors.name && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '4px', display: 'block' }}>{errors.name}</span>}
          </div>

          <div className="form-group">
            <label className="form-label" style={{ fontWeight: '600', color: 'var(--text-dark)' }}><Trans i18nKey="inventory.partNumber" /> *</label>
            <div className="inv-part-suggest" ref={partSuggestRef}>
              <input
                type="text"
                className={`form-input ${errors.partNumber ? 'form-input-error' : ''}`}
                placeholder={i18n.t('inventory.searchOrEnterPartNumber')}
                value={partNumber}
                onChange={(e) => {
                  setPartNumber(e.target.value);
                  if (errors.partNumber) setErrors({ ...errors, partNumber: null });
                }}
                onFocus={() => {
                  if (partSuggestions.length > 0) setPartSuggestOpen(true);
                }}
                autoComplete="off"
                style={{ borderColor: errors.partNumber ? 'var(--accent-red)' : '' }}
              />
              {partSuggestOpen && (
                <div className="inv-part-suggest__list">
                  {partSuggestLoading && (
                    <div className="inv-metric-tree__empty"><Trans i18nKey="inventory.searching" /></div>
                  )}
                  {!partSuggestLoading &&
                    partSuggestions.map((s) => (
                      <button
                        key={`${s.id || s.partNumber}`}
                        type="button"
                        className="inv-part-suggest__item"
                        onClick={() => {
                          setPartNumber(s.partNumber);
                          if (s.name) setName(s.name);
                          if (s.category) {
                            // Part-number hit may include productCategory text only
                            setCategory(s.category);
                          }
                          setPartSuggestOpen(false);
                        }}
                      >
                        <span className="inv-part-suggest__pn">{s.partNumber}</span>
                        {s.name ? <span className="inv-part-suggest__name">{s.name}</span> : null}
                      </button>
                    ))}
                </div>
              )}
            </div>
            {errors.partNumber && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '4px', display: 'block' }}>{errors.partNumber}</span>}
          </div>
        </div>

        <div className="inv-responsive-grid inv-product-dropdown-row">
          <div className="form-group">
            <label className="form-label" style={{ fontWeight: '600', color: 'var(--text-dark)' }}><Trans i18nKey="common.category" /> *</label>
            <select
              className="form-select inv-form-dropdown-control"
              value={productServiceId}
              onChange={(e) => handleCategoryChange(e.target.value)}
              style={{ borderColor: errors.category ? 'var(--accent-red)' : '', height: '40px' }}
              disabled={categories.length === 0}
            >
              <option value="">
                {categories.length === 0 ? i18n.t('inventory.noCategoriesAvailable') : i18n.t('onboarding.selectCategory')}
              </option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name || cat.label}
                </option>
              ))}
            </select>
            {errors.category && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '4px', display: 'block' }}>{errors.category}</span>}
          </div>

          <div className="form-group inv-uom-field">
            <label className="form-label" style={{ fontWeight: '600', color: 'var(--text-dark)' }}><Trans i18nKey="productMetrics.unitOfMeasure" /> *</label>
            <MetricTreeSelect
              categories={metricCategories}
              value={metricId}
              categoryValue={metricCategory}
              displayLabel={metricLabel}
              error={Boolean(errors.unitOfMeasure)}
              loadingCategories={lookupsLoading}
              onChange={({ metricId: id, metricCategory: cat, label }) => {
                setMetricId(id);
                setMetricCategory(cat);
                setMetricLabel(label || '');
                if (errors.unitOfMeasure) setErrors({ ...errors, unitOfMeasure: null });
              }}
            />
            {errors.unitOfMeasure && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '4px', display: 'block' }}>{errors.unitOfMeasure}</span>}
          </div>
        </div>

        <div className="inv-responsive-grid">
          <div className="form-group">
            <label className="form-label" style={{ fontWeight: '600', color: 'var(--text-dark)' }}><Trans i18nKey="crm.manufacturer" /> *</label>
            <input
              type="text"
              className={`form-input ${errors.manufacturer ? 'form-input-error' : ''}`}
              placeholder={i18n.t('inventory.manufacturerPlaceholder')}
              value={manufacturer}
              onChange={(e) => {
                setManufacturer(e.target.value);
                if (errors.manufacturer) setErrors({ ...errors, manufacturer: null });
              }}
              style={{ borderColor: errors.manufacturer ? 'var(--accent-red)' : '', height: '40px' }}
            />
            {errors.manufacturer && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '4px', display: 'block' }}>{errors.manufacturer}</span>}
          </div>

          <div className="form-group">
            <label className="form-label" style={{ fontWeight: '600', color: 'var(--text-dark)' }}><Trans i18nKey="status.status" /> *</label>
            <select
              className="form-select"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                if (errors.status) setErrors({ ...errors, status: null });
              }}
              style={{ borderColor: errors.status ? 'var(--accent-red)' : '', height: '40px' }}
              disabled={statusSelectOptions.length === 0}
            >
              <option value="">
                {statusSelectOptions.length === 0 ? i18n.t('inventory.noStatusesAvailable') : i18n.t('common.selectStatus')}
              </option>
              {statusSelectOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {translateProductStatusLabel(opt.value, opt.label)}
                </option>
              ))}
            </select>
            {status === 'End of Production' && (
              <span style={{ fontSize: '11px', color: '#b45309', marginTop: '4px', display: 'block' }}>
                <Trans i18nKey="inventory.endOfProductionPurchaseNote" />
              </span>
            )}
            {errors.status && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '4px', display: 'block' }}>{errors.status}</span>}
          </div>
        </div>

        <div className="inv-responsive-grid">
          <div className="form-group">
            <label className="form-label" style={{ fontWeight: '600', color: 'var(--text-dark)' }}><Trans i18nKey="inventory.hsnCode" /></label>
            <input
              type="text"
              inputMode="numeric"
              className={`form-input ${errors.hsnCode ? 'form-input-error' : ''}`}
              placeholder={i18n.t('inventory.hsnCodePlaceholder')}
              value={hsnCode}
              maxLength={8}
              onChange={(e) => {
                setHsnCode(sanitizeHsnInput(e.target.value));
                if (errors.hsnCode) setErrors({ ...errors, hsnCode: null });
              }}
              style={{ borderColor: errors.hsnCode ? 'var(--accent-red)' : '' }}
            />
            {/* <span style={{ fontSize: '11px', color: 'var(--text-grey)', marginTop: '4px', display: 'block' }}>
              Digits only, up to 8 characters.
            </span> */}
            {errors.hsnCode && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '4px', display: 'block' }}>{errors.hsnCode}</span>}
          </div>
        </div>

        <div className="inv-responsive-grid">
          <div className="form-group">
            <label className="form-label" style={{ fontWeight: '600', color: 'var(--text-dark)' }}><Trans i18nKey="inventory.listPricePurchaseCost" /> *</label>
            <input
              type="text"
              inputMode="decimal"
              className={`form-input no-spinner ${errors.listPrice ? 'form-input-error' : ''}`}
              placeholder={i18n.t('inventory.pricePlaceholder15000')}
              value={listPrice}
              onChange={(e) => {
                setListPrice(sanitizeDecimalInput(e.target.value));
                if (errors.listPrice) setErrors({ ...errors, listPrice: null });
              }}
              style={{ borderColor: errors.listPrice ? 'var(--accent-red)' : '' }}
            />
            {errors.listPrice && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '4px', display: 'block' }}>{errors.listPrice}</span>}
          </div>

          <div className="form-group">
            <label className="form-label" style={{ fontWeight: '600', color: 'var(--text-dark)' }}><Trans i18nKey="inventory.unitRateSellingRate" /> *</label>
            <input
              type="text"
              inputMode="decimal"
              className={`form-input no-spinner ${errors.unitRate ? 'form-input-error' : ''}`}
              placeholder={i18n.t('inventory.pricePlaceholder18500')}
              value={unitRate}
              onChange={(e) => {
                setUnitRate(sanitizeDecimalInput(e.target.value));
                if (errors.unitRate) setErrors({ ...errors, unitRate: null });
              }}
              style={{ borderColor: errors.unitRate ? 'var(--accent-red)' : '' }}
            />
            {errors.unitRate && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '4px', display: 'block' }}>{errors.unitRate}</span>}
          </div>
        </div>

        <div className="inv-responsive-grid inv-responsive-grid--compact">
          <div className="form-group">
            <label className="form-label" style={{ fontWeight: '600', color: 'var(--text-dark)' }}><Trans i18nKey="inventory.moqMinOrderQty" /></label>
            <input
              type="text"
              inputMode="numeric"
              className="form-input no-spinner"
              placeholder={i18n.t('examples.eG5')}
              value={moq}
              onChange={(e) => setMoq(sanitizeIntegerInput(e.target.value))}
            />
          </div>
          <div className="form-group">
            <label className="form-label" style={{ fontWeight: '600', color: 'var(--text-dark)' }}><Trans i18nKey="inventory.reorderQuantity" /></label>
            <input
              type="text"
              inputMode="numeric"
              className="form-input no-spinner"
              placeholder={i18n.t('inventory.reorderQtyPlaceholder')}
              value={reorderQuantity}
              onChange={(e) => setReorderQuantity(sanitizeIntegerInput(e.target.value))}
            />
          </div>
          <div className="form-group">
            <label className="form-label" style={{ fontWeight: '600', color: 'var(--text-dark)' }}><Trans i18nKey="inventory.maintenanceStockQty" /></label>
            <input
              type="text"
              inputMode="numeric"
              className="form-input no-spinner"
              placeholder={i18n.t('examples.eG10')}
              value={maintenanceStockQuantity}
              onChange={(e) => setMaintenanceStockQuantity(sanitizeIntegerInput(e.target.value))}
            />
            {/* <span style={{ fontSize: '11px', color: 'var(--text-grey)', marginTop: '4px', display: 'block' }}>
              Reserved for maintenance jobs.
            </span> */}
          </div>
        </div>

        <div className="inv-responsive-grid">
          <div className="form-group">
            <label className="form-label" style={{ fontWeight: '600', color: 'var(--text-dark)' }}><Trans i18nKey="inventory.preferredVendor1" /></label>
            <select
              className="form-select"
              value={preferredVendor1Id}
              onChange={(e) => setPreferredVendor1Id(e.target.value)}
              style={{ height: '40px' }}
            >
              <option value="">
                {primaryVendors.length === 0 ? i18n.t('inventory.noVendorsAvailable') : i18n.t('inventory.selectVendor')}
              </option>
              {primaryVendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.label || v.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label" style={{ fontWeight: '600', color: 'var(--text-dark)' }}><Trans i18nKey="inventory.preferredVendor2" /></label>
            <select
              className="form-select"
              value={preferredVendor2Id}
              onChange={(e) => setPreferredVendor2Id(e.target.value)}
              style={{ height: '40px' }}
            >
              <option value="">
                {secondaryVendors.length === 0 ? i18n.t('inventory.noVendorsAvailable') : i18n.t('inventory.selectVendor')}
              </option>
              {secondaryVendors.map((v) => (
                <option key={`v2-${v.id}`} value={v.id}>
                  {v.label || v.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label" style={{ fontWeight: '600', color: 'var(--text-dark)' }}><Trans i18nKey="inventory.abcAnalysisCategory" /></label>
          <select
            className="form-select"
            value={abcCategory}
            onChange={(e) => setAbcCategory(e.target.value)}
            style={{ height: '40px' }}
          >
            <option value=""><Trans i18nKey="inventory.noneNotClassified" /></option>
            {abcOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label" style={{ fontWeight: '600', color: 'var(--text-dark)' }}><Trans i18nKey="common.description" /></label>
          <textarea
            className="form-input"
            style={{ minHeight: '80px', padding: '10px 12px', resize: 'vertical' }}
            placeholder={i18n.t('inventory.productDescriptionPlaceholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="form-buttons-right inv-form-footer" style={{ borderTop: '1px solid var(--border-color)', paddingTop: '24px', marginTop: '12px' }}>
          <button
            type="button"
            className="btn-secondary"
            onClick={onCancel}
            disabled={saving}
            style={{ height: '40px', borderRadius: '8px', padding: '0 24px', fontWeight: '600' }}
          >
            <Trans i18nKey="formActions.cancel" />
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={saving}
            style={{ height: '40px', borderRadius: '8px', padding: '0 24px', fontWeight: '600', opacity: saving ? 0.7 : 1 }}
          >
            {saving ? i18n.t('inventory.savingEllipsis') : i18n.t('inventory.saveProduct')}
          </button>
        </div>
      </form>
    </div>
      </div>
    </AppLayout>
  );
}
